import {
	ParenthesesForElements,
	PayableToTypes,
	PurchasePayableToTypes,
	SoftwareTransactionFeeDisplayLocation,
} from '@models/businessSettings/settings';
import { MatterFileType } from '@models/matter';
import { createSelector } from '@ngxs/store';
import { BusinessState } from '@state/business/business.state';
import { TrustLedgerSelectors } from '@state/global/trust-ledger/trust_ledger';
import { TrustLedgerTransactionFeeChangeDetails } from '@state/global/trust-ledger/trust_ledger.models';
import {
	getDefaultTrustLedgerPayments,
	getTrustLedgerPaymentsDetails,
} from '@state/global/trust-ledger/trust_ledger.util';

export interface GeneralSettingsViewModel {
	propertyTaxIncrease: number;
	cashShortFallDescription: string;
	transactionFee: {
		displayLocation: SoftwareTransactionFeeDisplayLocation;
		priceChange: number;
		includeHstInBaseFee: boolean;
	};
	payableTo: {
		purchase: PurchasePayableToTypes;
		saleRefinance: PayableToTypes;
	};
	parentheses: {
		sale: ParenthesesForElements;
		purchase: ParenthesesForElements;
		refinance: ParenthesesForElements;
	};
	trustLedgerTransactionFeeChangeDetails: TrustLedgerTransactionFeeChangeDetails;
	jurisdictionHSTComplianceLetters: boolean;
}

const $getTrustLedgerTransactionFeeChangeDetails = createSelector(
	[
		TrustLedgerSelectors.getDefaultTrustLedgerSale,
		TrustLedgerSelectors.getDefaultTrustLedgerPurchase,
		TrustLedgerSelectors.getDefaultTrustLedgerRefinance,
	],
	(
		defaultTrustLedgerSale,
		defaultTrustLedgerPurchase,
		defaultTrustLedgerRefinance,
	): TrustLedgerTransactionFeeChangeDetails => {
		const trustLedgerSalePaymentDetails = getTrustLedgerPaymentsDetails(
			defaultTrustLedgerSale,
			MatterFileType.SALE,
		);
		const trustLedgerPurchasePaymentDetails = getTrustLedgerPaymentsDetails(
			defaultTrustLedgerPurchase,
			MatterFileType.PURCHASE,
		);
		const trustLedgerRefinancePaymentDetails = getTrustLedgerPaymentsDetails(
			defaultTrustLedgerRefinance,
			MatterFileType.REFINANCE,
		);

		const defaultTrustLedgerSalePayment = getDefaultTrustLedgerPayments(
			trustLedgerSalePaymentDetails?.nextItemIndex,
			MatterFileType.SALE,
		);

		const defaultTrustLedgerPurchasePayment = getDefaultTrustLedgerPayments(
			trustLedgerPurchasePaymentDetails?.nextItemIndex,
			MatterFileType.PURCHASE,
		);

		const defaultTrustLedgerRefinancePayment = getDefaultTrustLedgerPayments(
			trustLedgerRefinancePaymentDetails?.nextItemIndex,
			MatterFileType.REFINANCE,
		);

		return {
			trustLedgerSalePaymentDetails,
			trustLedgerPurchasePaymentDetails,
			trustLedgerRefinancePaymentDetails,
			defaultTrustLedgerSalePayment,
			defaultTrustLedgerPurchasePayment,
			defaultTrustLedgerRefinancePayment,
		};
	},
);

export class GeneralSettingsSelectors {
	static $viewModel = createSelector(
		[
			BusinessState.getDefaultSettings,
			$getTrustLedgerTransactionFeeChangeDetails,
		],
		(
			defaultSettings,
			trustLedgerTransactionFeeChangeDetails,
		): GeneralSettingsViewModel => {
			return {
				propertyTaxIncrease: defaultSettings.estimatedPropertyTaxIncrease,
				cashShortFallDescription:
					defaultSettings.trustLedger.defaultDescriptionCashFall,
				transactionFee: {
					displayLocation: defaultSettings.softwareTransactionFee.displayIn,
					priceChange:
						defaultSettings.softwareTransactionFee.feeAdjustmentAmount,
					includeHstInBaseFee:
						defaultSettings.softwareTransactionFee.includeHstInBaseFee,
				},
				payableTo: {
					purchase: defaultSettings.trustLedger.purchasePayableToInTrust,
					saleRefinance: defaultSettings.trustLedger.payableToInTrust,
				},
				parentheses: {
					sale: defaultSettings.useParenthesesFor.sale,
					purchase: defaultSettings.useParenthesesFor.purchase,
					refinance: defaultSettings.useParenthesesFor.refinance,
				},
				trustLedgerTransactionFeeChangeDetails,
				jurisdictionHSTComplianceLetters:
					defaultSettings.jurisdictionHSTComplianceLetters,
			};
		},
	);
}
